import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from 'react';
import { useAtom } from 'jotai';
import { Loader2 } from 'lucide-react';
import { useTracker } from '@archipro-website/tracker';

import { cn } from '@rocco/utils/cn';
import { useUserLoggedIn } from '@modules/user';
import type { PublicEnquiryForm } from '~/modules/enquiry/types/public-enquiry-form';
import type { PublicUser } from '~/routes/remix-api.user.public-user';
import { useEnquiryModal } from '~/modules/enquiry/hooks/use-enquiry-modal';
import { getUserRegion } from '~/utils/countryHelper';
import { AuthModalBrandPane } from '~/modules-rocco/user/component/auth-modal/AuthModalBrandPane';
import { getAuthBrandStatement } from '~/modules-rocco/user/component/auth-modal/auth-intent-copy';
import type { GateIdentity } from '~/modules-rocco/user/component/auth-modal/GateIdentityFields';
import { isReturningPasskeyDevice } from '~/modules/user/passkey/passkey-device-match';
import { useAuthGateSegment } from '~/modules-rocco/user/auth-gate/use-auth-gate-segment';
import { trackAuthGateActionResumed } from '~/modules-rocco/user/auth-gate/auth-gate-events';
import { useGateSubjectValue } from '~/modules-rocco/user/auth-gate/gate-subject';
import { resolveGatePaneImage } from '~/modules-rocco/user/auth-gate/gate-pane-image';
import { useSocialAuthReturn } from '~/modules-rocco/user/auth-gate/use-social-auth-return';

import {
    BOOK_MEETING_AUTH_SOURCE,
    EMPTY_BOOK_MEETING_PROGRESS,
    bookMeetingGateProgressAtom,
    type BookMeetingGateIntent,
    type BookMeetingGateProgress,
} from '../book-meeting-gate-state';
import {
    EMPTY_BOOKING_CONTACT,
    buildBookingEnquiryFields,
    describeChosenMeeting,
    firstGateStep,
    formatSlotWhen,
    itemTypeForPageType,
    predictedSessionId,
    shouldAskBookingDetails,
    validateBookingContact,
    type BookMeetingGateStep,
    type BookingContact,
    type BookingContactErrors,
    type BookingSlot,
    type MeetingDetails,
} from '../domain/book-meeting-flow';
import { useBookMeetingForm } from './use-book-meeting-form';
import { useBookingFormAnswers } from './use-booking-form-answers';
import { useBookMeetingSubmission } from './use-book-meeting-submission';
import {
    BookMeetingGateBody,
    type BookMeetingPreviewData,
    type BookMeetingSubject,
} from '../components/BookMeetingGateBody';
import { BookMeetingGateContact } from '../components/BookMeetingGateContact';
import { BookMeetingGateDeclined } from '../components/BookMeetingGateDeclined';
import { BookMeetingGateDetails } from '../components/BookMeetingGateDetails';
import { BookMeetingGatePane } from '../components/BookMeetingGatePane';
import { BookMeetingGateSuccess } from '../components/BookMeetingGateSuccess';
import {
    BookMeetingGateUnavailable,
    type BookMeetingUnavailableCode,
} from '../components/BookMeetingGateUnavailable';

const REPICK_NOTICE =
    'Your answers matched a different calendar, and the time you chose isn’t free on it. Please pick another time.';

/** What the Business Manager preview hands in instead of fetching. */
export interface BookMeetingGatePreviewConfig {
    /** The draft form, or null for a Book button with no questions. */
    form: PublicEnquiryForm | null;
    data: BookMeetingPreviewData;
    /** The step to open on. */
    step?: BookMeetingGateStep;
    /** What the declined step says, since the preview never hears from the server. */
    declinedMessage?: string;
}

export interface UseBookMeetingGateControllerArgs {
    intent: BookMeetingGateIntent | null;
    isOpen: boolean;
    onClose: () => void;
    /** Shows the gate again with the persisted intent, after a social sign-in. */
    onReopen?: () => void;
    preview?: BookMeetingGatePreviewConfig;
}

export interface BookMeetingGateView {
    title: string;
    pane: ReactNode;
    content: ReactNode;
    /** Changes with the surface on screen, so the pane rewinds its scroll. */
    scrollResetKey: string;
    /** A send is on the wire; closing now would strand it. */
    dismissBlocked: boolean;
    /** Closing from the surface: records an abandoned sign-in, then closes. */
    onDismiss: () => void;
}

const contactFromAccount = (
    account: {
        FirstName?: string | null;
        LastName?: string | null;
        Email?: string | null;
        Phone?: string | null;
        Company?: string | null;
    } | null,
): BookingContact =>
    account
        ? {
              firstName: account.FirstName ?? '',
              lastName: account.LastName ?? '',
              email: account.Email ?? '',
              phone: account.Phone ?? '',
              company: account.Company ?? '',
          }
        : EMPTY_BOOKING_CONTACT;

/** A slot to draw the later preview steps with, from the first time offered. */
const previewSlot = (
    data: BookMeetingPreviewData,
    professionalName: string,
): BookingSlot | null => {
    const group = data.requestAvailability.freeDaysByDuration[0];
    const zone = group?.freeDays[0];
    const first = zone?.days[0]?.freeSlots[0];
    if (!group || !zone || !first) return null;
    return {
        sessionId: data.sessionAvailability?.sessionID ?? null,
        source: 'assigned',
        teamMemberId: null,
        organizerMemberId: null,
        startTimestamp: first.timestamp,
        duration: group.duration,
        timezoneName: zone.timezone,
        subjectName: professionalName,
        isCompany: true,
    };
};

/**
 * The booking gate's whole journey, independent of the surface it is drawn
 * on: [the form's questions] → person, day and time → your details → [sign in]
 * → booking → done.
 *
 * Nothing is sent until the end. A guest's send creates the enquiry first,
 * which makes their account (or asks them to log in when the email already
 * has one); only then is the time booked or requested, against wherever the
 * server routed the answers. Everything chosen along the way is kept with the
 * gate's persisted progress, so a sign-in that reloads the page resumes the
 * send instead of starting over.
 *
 * The modal draws this in a dialog; the Business Manager preview draws it
 * inline with made-up availability and no network.
 */
export function useBookMeetingGateController({
    intent,
    isOpen,
    onClose,
    onReopen,
    preview,
}: UseBookMeetingGateControllerArgs): BookMeetingGateView | null {
    const { isLoggedIn, user } = useUserLoggedIn();
    const tracker = useTracker();
    const subject = useGateSubjectValue();
    const account = isLoggedIn ? user : null;

    // --- Progress: persisted for the live gate, local for the preview. ---
    const [storedProgress, setStoredProgress] = useAtom(
        bookMeetingGateProgressAtom,
    );
    const [localProgress, setLocalProgress] =
        useState<BookMeetingGateProgress | null>(null);
    const progress =
        (preview ? localProgress : storedProgress) ??
        EMPTY_BOOK_MEETING_PROGRESS;
    const progressRef = useRef(progress);
    progressRef.current = progress;
    const patchProgress = useCallback(
        (patch: Partial<BookMeetingGateProgress>) => {
            const next = { ...progressRef.current, ...patch };
            progressRef.current = next;
            if (preview) setLocalProgress(next);
            else setStoredProgress(next);
        },
        [preview, setStoredProgress],
    );

    // --- The form, and the answers to it. ---
    const lookup = useBookMeetingForm(intent, {
        enabled: isOpen && !preview,
    });
    const form = preview ? preview.form : lookup.form;
    const formLoading = preview ? false : lookup.isLoading;
    const ipRegion = useMemo(
        () => (preview ? null : getUserRegion()),
        [preview],
    );
    const model = useBookingFormAnswers(form, {
        answers: progress.answers,
        onChange: (answers) => patchProgress({ answers }),
        ipRegion,
    });
    const asksDetails = shouldAskBookingDetails(form, {
        detailsFirst: intent?.detailsFirst,
    });

    // --- Steps. ---
    const [step, setStep] = useState<BookMeetingGateStep | null>(
        preview?.step ?? null,
    );
    const [subjectInfo, setSubjectInfo] = useState<BookMeetingSubject | null>(
        null,
    );
    const [details, setDetails] = useState<MeetingDetails | null>(null);
    const [contactDraft, setContactDraft] = useState<BookingContact | null>(
        null,
    );
    const [contactErrors, setContactErrors] = useState<BookingContactErrors>(
        {},
    );
    const [sendError, setSendError] = useState<string | null>(null);
    const [declinedMessage, setDeclinedMessage] = useState<string | null>(null);
    const [unavailable, setUnavailable] =
        useState<BookMeetingUnavailableCode | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [sentBooked, setSentBooked] = useState(false);
    /** Set once auth resolves for a send that stopped for it. */
    const [pendingResume, setPendingResume] = useState(false);
    /** The wall went up because a send needed it; resolving resumes that send. */
    const resumeOnResolveRef = useRef(false);
    /** This send followed an auth step, so its landing is a resumed action. */
    const resumedRef = useRef(false);

    // The preview's step picker moves the surface directly.
    useEffect(() => {
        if (preview?.step) setStep(preview.step);
    }, [preview?.step]);

    useEffect(() => {
        if (!isOpen || step !== null || formLoading || !intent) return;
        setStep(
            firstGateStep({
                asksDetails,
                detailsDone: progress.detailsDone,
                sending: progress.sending,
            }),
        );
    }, [isOpen, step, formLoading, intent, asksDetails, progress]);

    // --- Sending. ---
    const submission = useBookMeetingSubmission({
        onEnquiryCreated: ({ enquiryId, routed, generatedMember }) => {
            patchProgress({ enquiryId, routed });
            // The submit made an account and signed the reader in. That is
            // their "just signed in" moment, so the Secure Sign-In ask belongs
            // here; the booking carries on behind it.
            if (generatedMember) {
                resumedRef.current = true;
                gate.enterAuthenticated({ isNewAccount: true });
            }
        },
        onDeclined: (message) => {
            patchProgress({ sending: false });
            setDeclinedMessage(message);
            setStep('declined');
        },
        onAuthRequired: (publicUser?: PublicUser) => {
            resumeOnResolveRef.current = true;
            gate.open({
                publicUser,
                flow:
                    publicUser?.type === 'new'
                        ? 'post-enquiry-sign-up'
                        : 'post-enquiry-login',
                message: 'Log in to book your meeting.',
            });
        },
        onRepick: (sessionId) => {
            patchProgress({
                sending: false,
                routed: {
                    kind: 'Calendar',
                    calendarSessionId: sessionId,
                    branchId: null,
                },
            });
            setNotice(REPICK_NOTICE);
            setStep('schedule');
        },
        onUnavailable: (code) => {
            patchProgress({ sending: false });
            setUnavailable(code);
            setStep('unavailable');
        },
        onFailed: (message) => {
            if (resumedRef.current) {
                trackAuthGateActionResumed({
                    tracker,
                    authSource: BOOK_MEETING_AUTH_SOURCE,
                    landed: false,
                });
            }
            patchProgress({ sending: false });
            setSendError(message);
            setStep('contact');
        },
        onSent: ({ booked }) => {
            if (resumedRef.current) {
                trackAuthGateActionResumed({
                    tracker,
                    authSource: BOOK_MEETING_AUTH_SOURCE,
                    landed: true,
                });
            }
            patchProgress({ sending: false });
            setSentBooked(booked);
            setStep('success');
        },
    });

    const runSend = useCallback(() => {
        const current = progressRef.current;
        const { slot, contact } = current;
        if (!intent || !slot || !contact) return;
        // Signed in, the account decides who the enquiry is from: an address
        // typed as a guest would be refused against the session.
        const sendContact = account?.Email
            ? { ...contact, email: account.Email }
            : contact;
        // A form's answers always travel on an enquiry. Without one, a guest
        // still needs it: creating the enquiry is what makes their account.
        const needsEnquiry = Boolean(form) || !account;
        setSendError(null);
        submission.send({
            slot,
            contact: sendContact,
            enquiryFields: needsEnquiry
                ? buildBookingEnquiryFields({
                      form,
                      rules: model.rules,
                      answers: model.answers,
                      predicted: model.predicted,
                      contact: sendContact,
                      slot,
                      siteTreeId: intent.siteTreeId,
                      professionalId: intent.professionalId,
                      itemTitle: intent.itemTitle,
                      signedIn: Boolean(account),
                  })
                : null,
            enquiryId: current.enquiryId,
            routed: current.routed,
            professionalUrlSegment: intent.professionalUrlSegment,
            siteTreeId: intent.siteTreeId,
            itemTitle: intent.itemTitle,
        });
    }, [intent, account, form, model, submission]);

    // Resumes a send once the session it waited for exists. Auth resolving is
    // not enough on its own: the root loader catches up a beat later, and a
    // send made before it goes out as a guest.
    useEffect(() => {
        if (!pendingResume || !isLoggedIn || !isOpen || preview) return;
        // The form decides whether the send carries an enquiry, so it has to
        // be known first.
        if (formLoading) return;
        setPendingResume(false);
        resumedRef.current = true;
        runSend();
    }, [pendingResume, isLoggedIn, isOpen, preview, formLoading, runSend]);

    const gate = useAuthGateSegment({
        authSource: BOOK_MEETING_AUTH_SOURCE,
        onResolved: () => {
            if (!resumeOnResolveRef.current) return;
            resumeOnResolveRef.current = false;
            setPendingResume(true);
        },
        // Backing out of the wall returns to the details, still filled in,
        // and disarms the send.
        onAuthBack: () => {
            resumeOnResolveRef.current = false;
            submission.reset();
            patchProgress({ sending: false });
            setStep('contact');
        },
    });

    // --- The far side of a social sign-in this gate started. ---
    const social = useSocialAuthReturn(BOOK_MEETING_AUTH_SOURCE);
    const socialReturnRef = useRef<{ signUp: boolean } | null>(null);
    useEffect(() => {
        if (preview) return;
        const returned = social.take();
        if (returned.authReturn) {
            socialReturnRef.current = { signUp: returned.signUpReturn };
        }
        // Read once, at mount: the redirect's params are stripped straight after.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // The persisted progress hydrates after the first render, so the resume
    // waits for it rather than deciding on the empty default.
    useEffect(() => {
        const returned = socialReturnRef.current;
        if (!returned || !intent || !progress.sending) return;
        socialReturnRef.current = null;
        onReopen?.();
        setStep('sending');
        gate.enterAuthenticated({ isNewAccount: returned.signUp });
        setPendingResume(true);
    }, [intent, progress.sending, onReopen, gate]);

    // --- A closed gate forgets its surface; the next opening starts fresh. ---
    // Only on a close that follows an opening: "not open" is also the state at
    // mount, which on a social return is when the resume above is arming.
    const wasOpenRef = useRef(false);
    useEffect(() => {
        if (isOpen) {
            wasOpenRef.current = true;
            return;
        }
        if (!wasOpenRef.current) return;
        wasOpenRef.current = false;
        gate.reset();
        submission.reset();
        setStep(preview?.step ?? null);
        setSubjectInfo(null);
        setDetails(null);
        setContactDraft(null);
        setContactErrors({});
        setSendError(null);
        setDeclinedMessage(null);
        setUnavailable(null);
        setNotice(null);
        setSentBooked(false);
        setPendingResume(false);
        resumeOnResolveRef.current = false;
        resumedRef.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    // --- Handing over to the enquiry gate when booking is not possible. ---
    const professionalName =
        subjectInfo?.professionalName ?? intent?.professionalName ?? 'the team';
    const enquiryContext = useMemo(
        () =>
            intent?.professionalId
                ? {
                      professional: {
                          ID: intent.professionalId,
                          Title: intent.professionalName ?? 'the team',
                          branchOptions: [],
                          CompanyLogo: intent.companyLogo ?? '',
                          LogoBackgroundColor: intent.logoBackgroundColor ?? '',
                      },
                      itemType: itemTypeForPageType(intent.pageType),
                      siteTreeID: intent.siteTreeId,
                      itemTitle: intent.itemTitle,
                      source: 'BookMeetingGate' as const,
                  }
                : undefined,
        [intent],
    );
    const { openEnquiryModal } = useEnquiryModal(enquiryContext, {
        autoOpenFromUrl: false,
    });
    const handleEnquireInstead =
        enquiryContext && !preview
            ? () => {
                  onClose();
                  openEnquiryModal();
              }
            : undefined;

    const handleClose = useCallback(() => {
        gate.close();
        onClose();
    }, [gate, onClose]);

    if (!intent) return null;

    // --- Step handlers. ---
    const handleDetailsDone = () => {
        patchProgress({ detailsDone: true, answers: model.answers });
        setStep('schedule');
    };

    const handleSlotChosen = (slot: BookingSlot) => {
        patchProgress({ slot });
        setNotice(null);
        // A re-pick after the server routed elsewhere: the enquiry and the
        // details already exist, so the new time goes straight out.
        if (progressRef.current.enquiryId && progressRef.current.contact) {
            patchProgress({ sending: true });
            setStep('sending');
            if (!preview) runSend();
            return;
        }
        setStep('contact');
    };

    const contact =
        contactDraft ?? progress.contact ?? contactFromAccount(account);
    const contactForSend: BookingContact = account?.Email
        ? { ...contact, email: account.Email }
        : contact;

    const handleSubmitContact = () => {
        const errors = validateBookingContact(contactForSend);
        setContactErrors(errors);
        if (Object.keys(errors).length > 0) return;
        patchProgress({ contact: contactForSend, sending: true });
        if (preview) {
            patchProgress({ sending: false });
            setSentBooked(Boolean(progress.slot?.sessionId));
            setStep('success');
            return;
        }
        setStep('sending');
        runSend();
    };

    // --- Rendering. ---
    const inAuth = gate.isActive;
    const preferredName = intent.preferredTeamMemberName;
    const openingName = preferredName || intent.professionalName;
    const paneSubject =
        subjectInfo ??
        (openingName
            ? {
                  subjectName: openingName,
                  professionalName: intent.professionalName ?? openingName,
                  isCompany: preferredName ? false : null,
                  companyLogo: intent.companyLogo,
                  logoBackgroundColor: intent.logoBackgroundColor,
              }
            : null);
    // The page's photo of a named person stays while the body still names
    // them; the booking link's own photo, when it has one, is the meeting's.
    const bodyIsPreferred =
        Boolean(preferredName) && subjectInfo?.subjectName === preferredName;
    const paneOverride =
        details?.imageUrl ??
        (intent.paneImage && (bodyIsPreferred || !subjectInfo)
            ? intent.paneImage
            : (subjectInfo?.image ?? intent.paneImage));
    const paneImage = resolveGatePaneImage({
        override: paneOverride,
        subject,
        gate: 'download',
    });
    const isBooked = progress.slot
        ? Boolean(progress.slot.sessionId)
        : (subjectInfo?.isBooked ?? false);

    const pane =
        inAuth || !paneSubject ? (
            <AuthModalBrandPane
                statement={getAuthBrandStatement(
                    BOOK_MEETING_AUTH_SOURCE,
                    isReturningPasskeyDevice(),
                )}
                image={paneImage}
            />
        ) : (
            <BookMeetingGatePane
                image={paneImage}
                subjectName={paneSubject.subjectName}
                professionalName={paneSubject.professionalName}
                isCompany={paneSubject.isCompany}
                isBooked={subjectInfo?.isBooked ?? false}
                companyLogo={paneSubject.companyLogo}
                logoBackgroundColor={paneSubject.logoBackgroundColor}
                details={details}
            />
        );

    const routedSession =
        progress.routed?.kind === 'Calendar'
            ? progress.routed.calendarSessionId
            : predictedSessionId(model.predicted);
    const bodyMounted =
        step === 'schedule' || step === 'contact' || step === 'sending';
    const body = bodyMounted ? (
        <div
            className={cn(
                'flex h-full flex-col',
                (step !== 'schedule' || inAuth) && 'hidden',
            )}
        >
            <BookMeetingGateBody
                intent={intent}
                routedSessionId={routedSession}
                lockedToPreferred={lookup.source === 'personal'}
                notice={notice}
                onSubject={setSubjectInfo}
                onDetails={setDetails}
                onSlotChosen={handleSlotChosen}
                onBack={asksDetails ? () => setStep('details') : undefined}
                onEnquireInstead={handleEnquireInstead}
                preview={preview?.data}
            />
        </div>
    ) : null;

    const slotForView =
        progress.slot ??
        (preview ? previewSlot(preview.data, professionalName) : null);
    const identity: GateIdentity | null = account
        ? {
              firstName: contact.firstName,
              lastName: contact.lastName,
              email: account.Email ?? contact.email,
              phone: contact.phone || undefined,
              avatarUrl:
                  account.ProfileImageLink || account.ProfileImage || undefined,
          }
        : null;

    const surface = (() => {
        if (inAuth) return gate.element;
        switch (step) {
            case null:
                return (
                    <div
                        className="mx-auto my-auto flex w-full max-w-90.75 flex-col items-center justify-center py-16"
                        role="status"
                        aria-label="Loading"
                    >
                        <Loader2 className="size-9 animate-spin text-[#828282]" />
                    </div>
                );
            case 'details':
                return (
                    <BookMeetingGateDetails
                        model={model}
                        personName={
                            lookup.source === 'personal'
                                ? preferredName
                                : undefined
                        }
                        onContinue={handleDetailsDone}
                        ctaLabel={intent?.ctaLabel}
                    />
                );
            case 'declined':
                return (
                    <BookMeetingGateDeclined
                        professionalName={professionalName}
                        message={
                            declinedMessage ?? preview?.declinedMessage ?? ''
                        }
                        onChangeAnswers={
                            asksDetails ? () => setStep('details') : undefined
                        }
                        onClose={handleClose}
                    />
                );
            case 'unavailable':
                return (
                    <BookMeetingGateUnavailable
                        code={unavailable ?? 'sessionDisabled'}
                        professionalName={professionalName}
                        onEnquireInstead={handleEnquireInstead}
                        onClose={handleClose}
                    />
                );
            case 'success':
                return (
                    <BookMeetingGateSuccess
                        subjectName={
                            slotForView?.subjectName ??
                            paneSubject?.subjectName ??
                            professionalName
                        }
                        professionalName={professionalName}
                        when={slotForView ? formatSlotWhen(slotForView) : ''}
                        isBooked={preview ? isBooked : sentBooked}
                        meetingLine={
                            details
                                ? [details.sessionName, details.location.label]
                                      .filter(Boolean)
                                      .join(' · ')
                                : null
                        }
                        onClose={preview ? () => undefined : handleClose}
                    />
                );
            case 'sending':
                return (
                    <div
                        className="mx-auto my-auto flex w-full max-w-90.75 flex-col items-center justify-center gap-4 py-16"
                        role="status"
                    >
                        <Loader2 className="size-9 animate-spin text-[#828282]" />
                        <p className="text-[1.0625rem] text-[#828282]">
                            {isBooked
                                ? 'Booking your meeting…'
                                : 'Sending your request…'}
                        </p>
                    </div>
                );
            case 'contact':
                return slotForView ? (
                    <BookMeetingGateContact
                        contact={contactForSend}
                        onChange={(next) => {
                            setContactDraft(next);
                            if (Object.keys(contactErrors).length) {
                                setContactErrors(validateBookingContact(next));
                            }
                        }}
                        errors={contactErrors}
                        identity={identity}
                        emailLocked={Boolean(account?.Email)}
                        summary={describeChosenMeeting({
                            slot: slotForView,
                            sessionName: details?.sessionName,
                            professionalName,
                            isBooked,
                        })}
                        isBooked={isBooked}
                        isSending={submission.isSending}
                        errorMessage={sendError}
                        onBack={() => setStep('schedule')}
                        onSubmit={handleSubmitContact}
                    />
                ) : null;
            default:
                return null;
        }
    })();

    return {
        title: inAuth ? 'Sign in to book your meeting' : 'Book a meeting',
        pane,
        content: (
            <>
                {body}
                {step === 'schedule' && !inAuth ? null : surface}
            </>
        ),
        scrollResetKey: inAuth ? `auth:${gate.step}` : (step ?? 'loading'),
        dismissBlocked: submission.isSending,
        onDismiss: handleClose,
    };
}
